/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useMemo } from 'react';
import {
  IConversationOptions,
  IConversationMessage,
  SMEConversationOptionsType,
} from 'src/types/serviceResponseTypes';
import ChoiceLabel from '../../ChoiceLabel/ChoiceLabel';
import Explanation from '../../Explanation/Explanation';
import Nudge from '../../Nudge/Nudge';
import PinButton from '../../PinButton/PinButton';
import ChoiceLeftSide from '../ChoiceLeftSide';
import styles from './ChoiceItem.module.scss';

const ChoiceItem = React.memo<{
  choice: IConversationOptions;
  conversation: IConversationMessage;
  isMultiChoice: boolean;
  userSelection: string[] | undefined;
  isSMEMode?: boolean;
  isHistoryMode?: boolean;
  checkCorrectChoice: (key: string) => boolean;
  isMultiChoiceHydration: boolean;
  selectionStyle: string;
  enablePinning: boolean;
  onUserSelection: (choice: IConversationOptions) => void;
  inputName: string;
  isFirstChoice: boolean;
  firstChoiceInputRef?: React.Ref<HTMLInputElement>;
}>(
  ({
    choice,
    conversation,
    isMultiChoice,
    userSelection,
    isSMEMode,
    isHistoryMode,
    checkCorrectChoice,
    isMultiChoiceHydration,
    selectionStyle,
    enablePinning,
    onUserSelection,
    inputName,
    isFirstChoice,
    firstChoiceInputRef,
  }) => {
    const isCorrect = checkCorrectChoice(choice.key);
    const isSelected = conversation.selected_choices?.includes(choice.key);
    const showSelectedState = isSelected && !isMultiChoiceHydration;

    // Text colour matches the radio status (Figma): correct→green, incorrect→red,
    // picked (pre-submit)→dark bold, unselected→muted. All bold except unselected.
    const choiceLabelClassName = useMemo(() => {
      if (isCorrect) return 'text-accent-green font-inter-semiBold';
      if (showSelectedState && !isCorrect) return 'text-accent-red font-inter-semiBold';
      if (userSelection?.includes(choice.key)) return 'text-main font-inter-semiBold';
      return 'text-[var(--accent-muted)]';
    }, [isCorrect, showSelectedState, userSelection, choice.key]);

    // Render ChoiceLabel once - always on the right side
    const choiceLabel = useMemo(
      () => <ChoiceLabel choice={choice} className={choiceLabelClassName} />,
      [choice, choiceLabelClassName]
    );

    // Show pin button only for correct choices with pinning enabled
    const showPinButton = isCorrect && enablePinning && !isSMEMode && !isHistoryMode;
    const choiceInputId = `${inputName}-${choice.key}`;
    const ChoiceLabeledby = `${inputName}-${choice.key}-label`;

    return (
      <div key={choice.key}>
        <div className='flex items-center'>
          <ChoiceLeftSide
            choice={choice}
            conversation={conversation}
            isMultiChoice={isMultiChoice}
            userSelection={userSelection}
            isCorrect={isCorrect}
            showSelectedState={!!showSelectedState}
            onUserSelection={onUserSelection}
            inputName={inputName}
            inputId={choiceInputId}
            inputRef={isFirstChoice ? firstChoiceInputRef : undefined}
          />
          {/* Added aria-hidden to lable due to duplicated lable content reading twice by SR */}
          <label
            id={ChoiceLabeledby}
            htmlFor={choiceInputId}
            className={`flex-grow ${styles.choiceWrapper} ${selectionStyle}`}
          >
            {/* Left Side: Radio/Checkbox or Icons - This is the only part that changes */}
            {/* Right Side: Label - Always rendered, never changes position */}
            <div className={`flex-grow overflow-hidden ${styles.customizedScroll}`}>
              {choiceLabel}
            </div>

            {/* Pin Button - Only shown for correct answers */}
            {showPinButton && (
              <div className='flex-0'>
                <PinButton label={choice.text} group={conversation.id} />
              </div>
            )}
          </label>
        </div>

        {/* SME Mode: Show nudge and explanation */}
        {isSMEMode && (
          <>
            <Nudge nudge={(choice as SMEConversationOptionsType).nudge ?? ''} />
            <Explanation
              explanation={(choice as SMEConversationOptionsType).explanation ?? ''}
              isCorrect={conversation.is_correct}
              pinGroupId={conversation.id}
              isPinned={false}
            />
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.choice.key === nextProps.choice.key &&
      prevProps.choice.text === nextProps.choice.text &&
      prevProps.userSelection === nextProps.userSelection &&
      prevProps.selectionStyle === nextProps.selectionStyle &&
      prevProps.conversation.locked === nextProps.conversation.locked &&
      prevProps.conversation.selected_choices === nextProps.conversation.selected_choices &&
      prevProps.isMultiChoiceHydration === nextProps.isMultiChoiceHydration &&
      prevProps.enablePinning === nextProps.enablePinning
    );
  }
);

export default ChoiceItem;
